/**
 * PLC 代码生成 Hook
 * 
 * 封装与后端 AI 服务的交互逻辑：
 * - 发送代码生成请求
 * - 处理流式响应
 * - 管理生成状态
 */

import { useState, useCallback, useRef } from 'react';
import { backendChatService } from '../../AIChatbotPlugin/services/backendChatService';
import { plcCodeService } from '../services/plcCodeService';
import type {
  PlcLanguageType,
  PlcCodeGenerationRequest,
  PlcCodeGenerationResult,
  ParsedCodeBlock
} from '../types';

// ============================================
// 类型定义
// ============================================

/** 生成状态 */
export type GenerationStatus = 'idle' | 'generating' | 'success' | 'error';

/** Hook 配置选项 */
export interface UsePlcCodeGenerationOptions {
  /** Dify 能力 ID（用于调用特定的 AI 能力） */
  readonly difyCapabilityId?: number;
  /** 生成完成回调 */
  readonly onGenerationComplete?: (result: PlcCodeGenerationResult) => void;
  /** 收到代码块回调 */
  readonly onCodeBlockReceived?: (block: ParsedCodeBlock) => void;
  /** 流式内容更新回调 */
  readonly onStreamUpdate?: (content: string) => void;
  /** 错误回调 */
  readonly onError?: (error: Error) => void;
}

/** Hook 返回值 */
export interface UsePlcCodeGenerationReturn {
  /** 当前生成状态 */
  readonly status: GenerationStatus;
  /** 是否正在生成 */
  readonly isGenerating: boolean;
  /** 当前累积的响应内容 */
  readonly streamContent: string;
  /** 最近一次生成结果 */
  readonly lastResult: PlcCodeGenerationResult | null;
  /** 错误信息 */
  readonly error: Error | null;
  
  // 操作方法
  /** 发起代码生成请求 */
  readonly generate: (request: PlcCodeGenerationRequest) => Promise<PlcCodeGenerationResult>;
  /** 取消当前生成 */
  readonly cancel: () => void;
  /** 重置状态 */
  readonly reset: () => void;
}

// ============================================
// 默认配置
// ============================================

/** 默认 PLC 代码生成能力 ID */
const DEFAULT_PLC_CAPABILITY_ID = 1;

/** 语言名称映射 */
const LANGUAGE_NAMES: Readonly<Record<PlcLanguageType, string>> = {
  st: '结构化文本 (ST)',
  il: '指令表 (IL)',
  lad: '梯形图 (LAD)',
  fbd: '功能块图 (FBD)'
};

// ============================================
// Hook 实现
// ============================================

export const usePlcCodeGeneration = (
  options: UsePlcCodeGenerationOptions = {}
): UsePlcCodeGenerationReturn => {
  const {
    difyCapabilityId = DEFAULT_PLC_CAPABILITY_ID,
    onGenerationComplete,
    onCodeBlockReceived,
    onStreamUpdate,
    onError
  } = options;

  // ========================================
  // 状态
  // ========================================
  
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [streamContent, setStreamContent] = useState<string>('');
  const [lastResult, setLastResult] = useState<PlcCodeGenerationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // 用于取消请求的引用
  const abortControllerRef = useRef<AbortController | null>(null);
  const taskIdRef = useRef<number | null>(null);
  // 用于存储流式关闭函数
  const closeStreamRef = useRef<(() => void) | null>(null);

  // ========================================
  // 构建提示词
  // ========================================
  const buildPrompt = useCallback(
    (request: PlcCodeGenerationRequest): string => {
      const { prompt, targetLanguage, currentCode, context } = request;
      const langName = LANGUAGE_NAMES[targetLanguage];
      
      const parts: string[] = [
        `请用 ${langName} 语言编写 PLC 代码。`,
        '',
        `## 用户需求`,
        prompt
      ];
      
      // 如果有现有代码，添加上下文
      if (currentCode?.trim()) {
        parts.push(
          '',
          `## 当前代码`,
          '请基于以下代码进行修改或优化：',
          '```' + targetLanguage,
          currentCode,
          '```'
        );
      }
      
      // 添加额外上下文
      if (context && Object.keys(context).length > 0) {
        parts.push(
          '',
          `## 额外信息`,
          JSON.stringify(context, null, 2)
        );
      }
      
      // 添加输出格式要求
      parts.push(
        '',
        `## 输出要求`,
        `1. 请将代码放在 \`\`\`${targetLanguage} 代码块中`,
        `2. 代码需符合 IEC 61131-3 标准`,
        `3. 添加必要的中文注释说明`,
        `4. 如有需要，简要解释代码逻辑`
      );
      
      return parts.join('\n');
    },
    []
  );

  // ========================================
  // 发起代码生成请求
  // ========================================
  const generate = useCallback(
    async (request: PlcCodeGenerationRequest): Promise<PlcCodeGenerationResult> => {
      // 取消之前的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // 关闭之前的流式连接
      closeStreamRef.current?.();
      closeStreamRef.current = null;
      
      // 重置状态
      setStatus('generating');
      setStreamContent('');
      setError(null);
      
      abortControllerRef.current = new AbortController();
      
      try {
        const prompt = buildPrompt(request);
        let fullContent = '';
        let detectedBlock: ParsedCodeBlock | null = null;
        
        // 使用 Promise 包装流式响应
        const streamResult = await new Promise<{
          success: boolean;
          content: string;
          error?: string;
        }>((resolve, reject) => {
          // 使用 sendAndStream 发送请求并订阅流式响应
          backendChatService
            .sendAndStream({
              request: {
                difyCapabilityId,
                userInput: prompt
              },
              // ★ 改为 onToken（替代旧 onChunk）
              onToken: (_delta: string, accumulated: string) => {
                fullContent = accumulated;
                setStreamContent(accumulated);
                onStreamUpdate?.(accumulated);
                
                // 尝试实时解析代码块
                const block = plcCodeService.extractFirstPlcCode(
                  accumulated,
                  request.targetLanguage
                );
                
                if (block && !detectedBlock) {
                  detectedBlock = block;
                  onCodeBlockReceived?.(block);
                }
              },
              onDone: () => {
                resolve({
                  success: true,
                  content: fullContent
                });
              },
              onError: (err: Error) => {
                reject(err);
              }
            })
            .then(({ taskId, close }) => {
              taskIdRef.current = taskId;
              closeStreamRef.current = close;
            })
            .catch(reject);
        });
        
        // 最终解析代码块
        const finalBlock = plcCodeService.extractFirstPlcCode(
          streamResult.content,
          request.targetLanguage
        );
        
        const result: PlcCodeGenerationResult = finalBlock
          ? {
              code: finalBlock.content,
              language: finalBlock.language !== 'unknown'
                ? finalBlock.language
                : request.targetLanguage,
              explanation: extractExplanation(streamResult.content, finalBlock),
              success: true
            }
          : {
              code: '',
              language: request.targetLanguage,
              success: false,
              error: '未能从 AI 响应中解析出代码块'
            };
        
        setLastResult(result);
        setStatus(result.success ? 'success' : 'error');
        onGenerationComplete?.(result);
        
        return result;
      } catch (err) {
        // 关闭流式连接
        if (closeStreamRef.current) {
          (closeStreamRef.current as () => void)();
        }
        closeStreamRef.current = null;
        
        const error = err instanceof Error ? err : new Error(String(err));
        
        // 忽略取消错误
        if (error.name === 'AbortError') {
          setStatus('idle');
          return {
            code: '',
            language: request.targetLanguage,
            success: false,
            error: '请求已取消'
          };
        }
        
        setError(error);
        setStatus('error');
        onError?.(error);
        
        const result: PlcCodeGenerationResult = {
          code: '',
          language: request.targetLanguage,
          success: false,
          error: error.message
        };
        
        setLastResult(result);
        return result;
      }
    },
    [
      difyCapabilityId,
      buildPrompt,
      onGenerationComplete,
      onCodeBlockReceived,
      onStreamUpdate,
      onError
    ]
  );

  // ========================================
  // 取消当前生成
  // ========================================
  const cancel = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // 关闭流式连接
    closeStreamRef.current?.();
    closeStreamRef.current = null;
    
    // 如果有任务 ID，尝试通知后端取消
    if (taskIdRef.current) {
      taskIdRef.current = null;
    }
    
    setStatus('idle');
  }, []);

  // ========================================
  // 重置状态
  // ========================================
  const reset = useCallback((): void => {
    cancel();
    setStreamContent('');
    setLastResult(null);
    setError(null);
  }, [cancel]);

  // ========================================
  // 返回
  // ========================================
  return {
    status,
    isGenerating: status === 'generating',
    streamContent,
    lastResult,
    error,
    
    generate,
    cancel,
    reset
  };
};

// ============================================
// 辅助函数
// ============================================

/**
 * 从完整响应中提取解释说明（代码块之外的文本）
 */
const extractExplanation = (
  fullContent: string,
  codeBlock: ParsedCodeBlock
): string => {
  const beforeCode = fullContent.slice(0, codeBlock.startIndex).trim();
  const afterCode = fullContent.slice(codeBlock.endIndex).trim();
  
  const parts: string[] = [];
  
  if (beforeCode) {
    parts.push(beforeCode);
  }
  
  if (afterCode) {
    parts.push(afterCode);
  }
  
  return parts.join('\n\n');
};

export default usePlcCodeGeneration;